import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, ArrowRight } from 'lucide-react';
import { Footer, SectionEyebrow } from '../components/shared';
import { AnnounceBar } from '../components/shared/AnnounceBar';
import { SiteNav, PAGE_LINKS } from '../components/shared/SiteNav';

const SUPPORT_EMAIL = 'support@briefcaseai.org';

const INDUSTRIES = [
  { value: 'advisory', label: 'Advisory' },
  { value: 'banking', label: 'Banking & Financial Services' },
  { value: 'legal', label: 'Legal' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'other', label: 'Other' },
];

const FIELD =
  'w-full rounded-md border border-rule-strong bg-paper px-4 py-3 text-[15px] text-ink outline-none transition-colors focus:border-ink';
const LABEL = 'mb-2 block text-[13px] font-medium tracking-[0.0175em] text-ink-2';

export const ContactPage: React.FC = () => {
  const canonicalUrl = 'https://briefcaseai.org/contact';
  const seoDescription =
    'Book a Briefcase AI demo for managed agent infrastructure with policy, approval, and evidence built in.';
  const socialImage = 'https://briefcaseai.org/briefcase-ai-thumbnail.png';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    type: '',
    message: '',
  });
  const [handedOff, setHandedOff] = useState(false);

  /**
   * Hands the request to the visitor's own mail client. The fields shape the
   * draft rather than being posted anywhere, so there is no server, no
   * credentials, and nothing to deliver.
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { name, email, company, type, message } = formData;
    const subject = `Governance pilot request: ${name}${company ? ` (${company})` : ''}`;
    const body = [
      `Name: ${name}`,
      `Email: ${email}`,
      company ? `Company: ${company}` : null,
      `Industry: ${type}`,
      '',
      message,
    ]
      .filter((line) => line !== null)
      .join('\n');
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    setHandedOff(true);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <>
      <Helmet>
        <title>Contact Briefcase AI: Governance for high-stakes decisions</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Contact Briefcase AI" />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={socialImage} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Briefcase AI" />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={socialImage} />
      </Helmet>

      <AnnounceBar />

      <div className="bc-shell">
        <SiteNav links={PAGE_LINKS} />

        <main className="px-5 pb-24 pt-10 sm:px-8 sm:pt-16">
          <div className="mx-auto max-w-[1120px]">
            <SectionEyebrow>Private access</SectionEyebrow>
            <h1 className="bc-h2 mt-6 max-w-[18ch]">Start with one workflow where an agent needs permission to act.</h1>
            <p className="bc-prose mt-6 max-w-[62ch]">
              Tell us where your agents need to work, which systems they need to reach, and where
              people need to stay in control. We will show you how the managed platform fits.
            </p>

            <div className="mt-16 grid gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:gap-16">
              <div className="bc-panel p-6 sm:p-9">
                <h2 className="bc-h3">Tell us about the workflow</h2>

                <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-5">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="c-name" className={LABEL}>
                        Name *
                      </label>
                      <input
                        id="c-name"
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className={FIELD}
                      />
                    </div>
                    <div>
                      <label htmlFor="c-email" className={LABEL}>
                        Email *
                      </label>
                      <input
                        id="c-email"
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className={FIELD}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="c-company" className={LABEL}>
                      Company
                    </label>
                    <input
                      id="c-company"
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className={FIELD}
                    />
                  </div>

                  <div>
                    <label htmlFor="c-type" className={LABEL}>
                      Industry *
                    </label>
                    <select
                      id="c-type"
                      name="type"
                      required
                      value={formData.type}
                      onChange={handleChange}
                      className={FIELD}
                    >
                      <option value="">Select industry</option>
                      {INDUSTRIES.map((i) => (
                        <option key={i.value} value={i.value}>
                          {i.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="c-message" className={LABEL}>
                      What should the agent do? *
                    </label>
                    <textarea
                      id="c-message"
                      name="message"
                      required
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="What systems should it use? Which actions need policy or human approval? What would a successful first workflow look like?"
                      className={`${FIELD} resize-y placeholder:text-ink-3`}
                    />
                  </div>

                  {handedOff && (
                    <p className="rounded-md border border-rule bg-paper-alt px-4 py-3 text-[14px] text-ink-2">
                      Opening your email app with this filled in. Send the draft and we'll be in
                      touch.
                    </p>
                  )}

                  <button type="submit" className="bc-btn mt-1 w-full py-4 text-[16px]">
                    Compose demo request
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </form>
              </div>

              <div className="bc-panel h-fit bg-paper-alt p-6 sm:p-9">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-rule bg-paper">
                  <Mail className="h-5 w-5 text-ink-2" />
                </span>
                <h2 className="bc-h3 mt-5">What happens next</h2>
                <p className="mt-4 text-[15px] leading-[1.7] text-ink-2">
                  We review the workflow, the systems involved, and the decisions that need
                  guardrails. The form opens your email client so you keep control of the draft.
                </p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="mt-6 inline-flex items-center gap-1.5 text-[15px] font-medium text-primary underline underline-offset-2"
                >
                  {SUPPORT_EMAIL}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </main>
      </div>

      <Footer />
    </>
  );
};
